import { useFormikContext } from "formik";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Barcode from "react-barcode";
import { Table } from "../../Table";
import { CreateOfferData, ProductOffer } from "../../../types/offers";

import {
  AddRounded,
  Search,
  DeleteOutlineRounded,
  Photo,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { findProduct } from "../../../services/offers";
import React, { useCallback, useState, useMemo, useRef } from "react";
import { formatPrice } from "../../../utils/helpers/Format";
import Avatar from "@mui/material/Avatar";
import { LIGHT_GREY } from "../../../utils/colors";

export function Products() {
  const inputEanRef = useRef<HTMLInputElement>(null);
  const [messageError, setMessageError] = useState<string>();
  const [ean, setEan] = useState<string>();
  const [productImage, setProductImage] = useState<string>();
  const [productName, setProductName] = useState<string>();
  const [productId, setProductId] = useState<string>();
  const [price, setPrice] = useState<number>(0);
  const [percentage, setPercentage] = useState<number>(0);
  const [discountPrice, setDiscountPrice] = useState<number>(0);

  const formatDiscountPrice = useMemo(() => {
    return formatPrice(discountPrice);
  }, [discountPrice]);

  const inputFormatPrice = useMemo(() => {
    return formatPrice(price);
  }, [price]);

  const formik = useFormikContext<CreateOfferData>();

  const product = useQuery(
    "product",
    async () => {
      const response = await findProduct(ean, productId);
      return response;
    },
    {
      enabled: false,
      onSuccess: (response) => {
        if (response) {
          setProductImage(
            `https://datasalesio-imagens.s3.amazonaws.com/imagem-produto/${response.SEQEAN}.jpg`
          );
          setPrice(response.PRECO);
          setProductName(response.PRODUTO);
        } else {
          setMessageError("Produto não encontrado");
        }
      },
    }
  );

  const handleSearchProduct = useCallback(() => {
    setMessageError("");
    formik.setErrors({});
    product.refetch();
  }, [product, formik]);

  const handleCalculateDiscount = (vPrice: number, vPercentage: number) => {
    if (vPercentage && vPrice) {
      const discount = vPrice * (vPercentage / 100);
      const total = vPrice - discount;
      setDiscountPrice(total);
    }
  };

  const handleChangePercentage = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const format = Number(e.target.value?.match(/\d/g)?.join(""));
      setPercentage(format);
      handleCalculateDiscount(price, format);
    },
    [price]
  );

  const handleInputClear = useCallback(() => {
    setEan("");
    setProductId("");
    setProductName("");
    setPrice(0);
    setPercentage(0);
    setDiscountPrice(0);
  }, []);

  const handleChangePrice = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const format = Number(e.target.value?.match(/\d/g)?.join("")) / 100;
      setPrice(format);
      handleCalculateDiscount(format, percentage);
    },
    [percentage]
  );

  const handleRemoveProduct = useCallback(
    (id: number) => {
      const tempOfferProducts = formik.values.products;
      const index = tempOfferProducts.findIndex((offer) => offer.id === id);
      tempOfferProducts.splice(index, 1);

      formik.setFieldValue("products", tempOfferProducts);
    },
    [formik]
  );

  const handleAddProduct = useCallback(() => {
    const newProductData: ProductOffer = {
      id: product.data.SEQPRODUTO,
      ean: product.data.SEQEAN,
      name: product.data.PRODUTO,
      price,
      percentage,
      discount_price: discountPrice,
    };
    product.remove();

    const tempOfferProducts = formik.values.products;
    tempOfferProducts.unshift(newProductData);

    formik.setFieldValue("products", tempOfferProducts);
    formik.setErrors({});
    handleInputClear();
    inputEanRef.current.focus();
  }, [
    formik,
    product,
    price,
    percentage,
    discountPrice,
    handleInputClear,
    inputEanRef,
  ]);

  return (
    <Stack spacing={4}>
      <Typography color="primary">Produtos</Typography>
      {(Boolean(messageError) || Boolean(formik.errors.products)) && (
        <Alert severity="warning">
          {messageError || (formik.errors.products as string)}
        </Alert>
      )}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          ref={inputEanRef}
          value={ean}
          onChange={({ target: { value } }) => setEan(value)}
          label="EAN"
          size="small"
        />
        <TextField
          value={productId}
          onChange={({ target: { value } }) => setProductId(value)}
          label="Id produto"
          size="small"
        />
        <Button
          disabled={product.isFetching}
          onClick={handleSearchProduct}
          startIcon={<Search />}
          variant="outlined"
          color="primary"
        >
          {product.isFetching ? "Buscando..." : "Buscar"}
        </Button>
        {productName && (
          <>
            <Avatar
              alt={ean}
              src={productImage}
              sx={{ bgcolor: LIGHT_GREY, width: 70, height: 70 }}
            >
              <Photo />
            </Avatar>

            <Typography color="default">{productName}</Typography>
          </>
        )}
      </Stack>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Stack direction="row" spacing={2}>
          <TextField
            disabled={!Boolean(product.data?.SEQEAN)}
            onChange={handleChangePrice}
            value={inputFormatPrice}
            label="Preço"
            size="small"
          />
          <TextField
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            onChange={handleChangePercentage}
            value={percentage}
            disabled={!Boolean(product.data?.SEQEAN)}
            label="Porcentagem"
            size="small"
          />
          <TextField
            disabled
            value={formatDiscountPrice}
            label="Preço com desconto"
            size="small"
          />
        </Stack>
        <Button
          disabled={!Boolean(discountPrice)}
          onClick={handleAddProduct}
          startIcon={<AddRounded />}
          variant="contained"
          color="primary"
        >
          Adicionar produto
        </Button>
      </Stack>
      <Stack>
        <Table
          style={{ width: "100%" }}
          toolbar={false}
          data={formik.values.products}
          columns={[
            {
              title: "ID",
              field: "id",
            },
            {
              title: "EAN",
              field: "ean",
              render: (row) => (
                <Barcode
                  value={row.ean}
                  displayValue
                  format="EAN13"
                  width={1}
                  height={15}
                  textMargin={0}
                  fontSize={10}
                />
              ),
            },
            {
              title: "Nome",
              field: "name",
              render: (row) => <Typography noWrap>{row.name}</Typography>,
            },
            {
              title: "Preço",
              field: "price",
              render: (row) => formatPrice(row.price),
            },
            {
              title: "Porcentagem",
              field: "percentage",
              render: (row) => `${row.percentage}%`,
            },
            {
              title: "Preço dois",
              field: "discount_price",
              render: (row) => formatPrice(row.discount_price),
            },
            {
              title: "Ações",
              render: (row) => (
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Excluir" placement="top">
                    <IconButton
                      onClick={() => {
                        handleRemoveProduct(row.id);
                      }}
                    >
                      <DeleteOutlineRounded />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ),
            },
          ]}
          options={{
            actionsColumnIndex: -1,
            pageSize: 4,
            selectionProps: () => ({ color: "primary" }),
          }}
        />
      </Stack>
    </Stack>
  );
}
